import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GridOptions, GridReadyEvent } from 'ag-grid';
import { SweetAlertHelper } from 'app/core/helpers/sweet-alert-helper.service';
import { NoteTemplateService } from 'app/core/services/note-template.service';
import { BooleanCheckComponent } from 'app/pages/A2-utils/grid-components/boolean-check/boolean-check.component';
import { T2CardComponent } from 'app/trip2-theme/components/t2-card/t2-card.component';
import { Subscription } from 'rxjs';
import { INoteTemplate } from '../../../core/interfaces/note.interface';
import { ReservationNoteService } from '../../trip-reservations/reservation-notes/reservation-note.service';

@Component({
  selector: 'app-note-template-chips',
  templateUrl: './note-template.component.html',
  styleUrls: ['./note-template.component.scss']
})
export class NoteTemplateComponent implements OnInit, OnDestroy {
  @ViewChild('cardCreate') cardCreate: T2CardComponent;

  protected activeAdd = false;
  gridOptions: GridOptions;
  protected count = 0;
  protected showInactive= false;
  subscriptions: Subscription[] = [];
  noteTemplateForm: FormGroup;
  titleModal = 'New Note Template';
  typesNotes = [];

  get noteTemplateIdCtrl (): AbstractControl {
    return this.noteTemplateForm.get('notetemplateid');
  }

  get descriptionCtrl (): AbstractControl {
    return this.noteTemplateForm.get('description');
  }

  constructor(
    protected service: NoteTemplateService,
    private _sweetAlertHelperServ: SweetAlertHelper,
    private reservationNoteService: ReservationNoteService,
    protected fb: FormBuilder
  ) {
    this.createGrid();
  }

  ngOnInit(): void {
    this.formBuild();
    this.callService();
  }

  formBuild(): void {
    this.noteTemplateForm = this.fb.group({
      notetemplateid: null,
      speedcode: [null, Validators.required],
      description: [null, Validators.required],
      isactive: true,
      notecategoryvalueid: null
    });
  }

  createGrid(): void {
    this.gridOptions = <GridOptions> {
      enableColResize: true,
      enableSorting: true,
      enableFilter: true,
      rowSelection: 'multiple',
      columnDefs: [
        {
          colId: 'notetemplateid',
          checkboxSelection: true,
          headerCheckboxSelection: true,
          width: 30,
          hide: true
        },
        {
          headerName: 'Description',
          field: 'description',
          width: 200
        },
        {
          headerName: 'Speed Code',
          field: 'speedcode',
          width: 100
        },
        {
          headerName: 'Type',
          field: 'notecategorydescription',
          width: 60
        },
        {
          headerName: 'Is Active',
          field: 'isactive',
          width: 30,
          cellRendererFramework: BooleanCheckComponent
        }
      ],
      onGridReady: (event: GridReadyEvent) => {
        if (event) {
          this.gridOptions.api.setRowData([]);
          this.gridOptions.api.sizeColumnsToFit();
        }
      },
      onRowDoubleClicked: (row): void => {
        this.openModal(row);
      }
    };
  }

  callService(): void {
    this.subscriptions.push(
      this.service.getTemplateNote({showInactive: this.showInactive})
        .subscribe({
          next: (data: INoteTemplate[]): void => {
            if (data && data.length) {
              this.count = data.length;
              this.gridOptions.api.setRowData(data);
            }
          },
          error: (er: any) => {
            this._sweetAlertHelperServ.captureException(er);
          }
        }),
      this.reservationNoteService.getTypesNotes(2).subscribe({
        next: (typeNotes) => this.typesNotes = typeNotes,
        error: error => this._sweetAlertHelperServ.captureException(error)
      })
    );
  }

  cancelNoteTemplate(): void {
    if (this.noteTemplateIdCtrl.value === null) {
      this.addNewNote();
    } else {
      this.activeAdd = false;
      this.cardCreate.close();
    }
    this.callService();
  }

  private addOrUpdateNoteTemplate(): void {
    if (this.noteTemplateForm.valid) {
      const formData = this.noteTemplateForm.getRawValue();
      const bodyReq = {
        notetemplateid: null,
        speedcode: formData.speedcode,
        description: formData.description,
        isactive: formData.isactive,
        notecategoryvalueid: formData.notecategoryvalueid
      };

      if (this.noteTemplateIdCtrl.value === null) {
        this.subscriptions.push(
          this.service.addNoteTemplate(bodyReq)
          .subscribe({
            next: (data: any) => {
              if (data) {
                this._sweetAlertHelperServ.createCustomAlert({
                  title: 'Success',
                  text: data.message,
                  type: 'success'
                }).then(() => {
                  this.callService();
                  this.addNewNote();
                });
              }
            },
            error: (er: any) => this._sweetAlertHelperServ.captureException(er)
          })
        );
      } else {
        bodyReq.notetemplateid = formData.notetemplateid;
        this.subscriptions.push(
          this.service.updateNoteTemplate(bodyReq)
          .subscribe({
            next: (data: any) => {
              if (data) {
                this._sweetAlertHelperServ.createCustomAlert({
                  title: 'Success',
                  text: data.message,
                  type: 'success'
                }).then(() => {
                  this.callService();
                  this.activeAdd = false;
                  this.cardCreate.close();
                });
              }
            },
            error: (er: any) => this._sweetAlertHelperServ.captureException(er)
          })
        );
      }
    } else {
      this._sweetAlertHelperServ.captureException('Invalid Form');
    }
  }

  addNewNote(): void {
    this.titleModal = 'New Note Template';
    this.cardCreate.open();
    this.resetForm();
  }

  resetForm(): void {
    this.noteTemplateForm.patchValue({
      notetemplateid: null,
      speedcode: null,
      description: null,
      isactive: true,
      notecategoryvalueid: null
    });
  }

  setterData(selectedRow: any): void {
    this.noteTemplateForm.patchValue({
      notetemplateid: selectedRow.notetemplateid,
      speedcode: selectedRow.speedcode,
      description: selectedRow.description,
      isactive: selectedRow.isactive,
      notecategoryvalueid: selectedRow.notecategoryvalueid ? selectedRow.notecategoryvalueid : null
    });
  }

  openModal(row: any): void {
    if (row && row.type === 'rowDoubleClicked') {
      this.cardCreate.open();
      this.titleModal = 'Update Note Template';
      this.setterData(row.data);
      this.activeAdd = true;
    }
  }

  closeModalNoteTemplate(event): void {
    if (event && !event.isOpen ) {
      this.activeAdd = false;
    }
  }

  saveNoteTemplate(): void {
    this.addOrUpdateNoteTemplate();
  }

  showDisable() {
    this.callService();
  }

  ngOnDestroy(): void {
    if (this.subscriptions.length) {
      this.subscriptions.forEach(f => f.unsubscribe());
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/note-template/note-template-component/note-template.component.ts